<template>
  <Teleport to="body">
<div class="modal fade" tabindex="-1" :id="id">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <div class="btn btn-icon btn-sm btn-active-light-primary ms-2" data-bs-dismiss="modal" aria-label="Fechar">
            <i class="bi bi-x-lg"></i>
          </div>
        </div>
        <div class="modal-body">
          <p v-if="confirm" class="plan-intro">
            Confira o que será gerado. Cada tipo de receituário sai em um documento separado.
          </p>

          <div class="plan-document" v-for="document in documents" :key="document.regulatory_type">
            <div class="plan-document-header">
              <RegulatoryBadge :classification="badgeFor(document)"/>
              <span class="plan-document-name">{{ document.regulatory_type_description }}</span>
            </div>
            <ul class="plan-document-items" v-if="document.items.length">
              <li v-for="item in document.items" :key="item.id_presc_drug">
                {{ item.drug_name }}
                <span v-if="item.manual" class="plan-document-manual">receituário escolhido manualmente</span>
              </li>
            </ul>
            <p v-if="numberingLine(document)" class="plan-document-numbering"
               :class="document.sncr && !document.sncr.sufficient ? 'plan-document-numbering-missing' : ''">
              <i class="bi" :class="document.sncr && !document.sncr.sufficient ? 'bi-exclamation-triangle-fill' : 'bi-hash'"></i>
              {{ numberingLine(document) }}
            </p>
          </div>

          <div v-if="confirm && blocked" class="plan-blocked">
            <div v-if="anvisaInstavel" class="plan-anvisa-down">
              <i class="bi bi-exclamation-octagon-fill"></i>
              <span>{{ anvisaInstavel }}</span>
            </div>

            <p class="plan-blocked-title">{{ blockedTitle }}</p>
            <p v-if="canRefill && !anvisaInstavel" class="plan-blocked-text">
              Para emitir, é preciso autenticar no gov.br e obter novos números. Leva cerca de um minuto.
            </p>
            <p v-else class="plan-blocked-text">
              Autenticar no gov.br não resolve este caso: sem uma inscrição aceita pelo SNCR
              (CRM ou CRO, com número e UF), a Anvisa não aloca numeração. Confira o conselho no seu perfil.
            </p>
            <p v-if="blockedVisa" class="plan-blocked-text">
              A numeração deste tipo depende de autorização prévia da vigilância sanitária do seu estado.
            </p>
            <p class="plan-blocked-text plan-blocked-draft">
              Sua prescrição fica salva. Você volta para esta tela ao terminar.
            </p>
            <button v-if="canRefill && !anvisaInstavel" type="button" class="btn btn-sm btn-primary" :disabled="authenticating" @click="authenticate">
              {{ authenticating ? 'Abrindo gov.br...' : 'Autenticar no gov.br' }}
            </button>

            <div class="plan-blocked-more">
              <button type="button" class="btn btn-sm btn-link plan-blocked-toggle"
                      @click="showOtherOptions = !showOtherOptions">
                Outras opções
                <i class="bi" :class="showOtherOptions ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>

              <div v-if="showOtherOptions" class="plan-blocked-menu">
                <div v-if="certifiers.length" class="plan-certifier plan-certifier-inline">
                  <label class="form-label">Assinatura digital</label>
                  <div class="form-check" v-for="certifier in certifiers" :key="certifier.id">
                    <input class="btn-check" type="radio" :id="'plan_certifier_unnumbered_' + id + '_' + certifier.id"
                           :value="certifier.id" v-model="selectedCertifier">
                    <label class="btn btn-outline btn-outline-dashed btn-active-light-primary p-3 d-flex align-items-center mb-3"
                           :for="'plan_certifier_unnumbered_' + id + '_' + certifier.id">{{ certifier.description }}</label>
                  </div>
                </div>
                <button type="button" class="btn btn-sm btn-light plan-blocked-option"
                        :disabled="generating || (certifiers.length > 0 && !selectedCertifier)"
                        @click="confirmWithoutNumbering">
                  <i class="bi bi-printer"></i>
                  Imprimir sem numeração da Anvisa
                </button>
                <p class="plan-blocked-draft mb-0">
                  O documento sai sem a numeração oficial e a dispensação não poderá ser
                  registrada no SNCR.
                </p>
              </div>
            </div>
          </div>

          <div v-if="confirm && !blocked && certifiers.length" class="plan-certifier">
            <label class="form-label">Assinatura digital</label>
            <div class="form-check" v-for="certifier in certifiers" :key="certifier.id">
              <input class="btn-check" type="radio" :id="'plan_certifier_' + id + '_' + certifier.id"
                     :value="certifier.id" v-model="selectedCertifier">
              <label class="btn btn-outline btn-outline-dashed btn-active-light-primary p-3 d-flex align-items-center mb-3"
                     :for="'plan_certifier_' + id + '_' + certifier.id">{{ certifier.description }}</label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            {{ confirm ? 'Voltar' : 'Fechar' }}
          </button>
          <button v-if="confirm && !blocked" type="button" class="btn btn-primary"
                  :disabled="generating || (certifiers.length > 0 && !selectedCertifier)"
                  @click="emitGenerate">
            {{ generating ? 'Gerando...' : generateLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
import RegulatoryBadge from "./RegulatoryBadge.vue";
import Alert from "@/helpers/alert";
import {classificationFromType, regulatoryTypeName, requiresSncrNumber} from "@/helpers/regulatoryTypes";
import {startGovBrAuth} from "@/helpers/sncr";

export default {
  name: "PrescriptionRegulatoryPlanModal",
  components: {RegulatoryBadge},
  emits: ['generate'],
  props: {
    id: String,
    plan: Object,
    certifiers: {
      type: Array,
      default: () => [],
    },
    mode: {
      type: String,
      default: 'confirm',
    },
    generating: Boolean,
    professionalId: [Number, String],
    returnTo: Object,
  },
  data() {
    return {
      selectedCertifier: '',
      authenticating: false,
      showOtherOptions: false,
    }
  },
  mounted() {
    this.selectedCertifier = this.$store.state.auth.certifier?.id ?? '';
  },
  computed: {
    confirm() {
      return this.mode === 'confirm';
    },
    documents() {
      return this.plan?.documents ?? [];
    },
    count() {
      return this.documents.length;
    },
    title() {
      if (!this.confirm) {
        return this.count === 1 ? 'Documento desta prescrição' : `Documentos desta prescrição (${this.count})`;
      }

      return this.count === 1 ? 'Gerar 1 documento' : `Gerar ${this.count} documentos`;
    },
    generateLabel() {
      return this.count === 1 ? 'Gerar' : `Gerar ${this.count} documentos`;
    },
    missing() {
      return this.plan?.sncr?.missing ?? [];
    },
    blocked() {
      return this.plan?.sncr?.blocked === true;
    },
    /**
     * Sem inscrição aceita, o gov.br não tem o que devolver. Oferecer o botão
     * ali manda o médico autenticar com sucesso e voltar igualmente travado.
     */
    canRefill() {
      return this.plan?.sncr?.can_refill !== false;
    },
    /**
     * A API distingue "seu pool acabou" de "a Anvisa não respondeu". Sem
     * mostrar essa diferença, o prescritor conclui — com razão — que o sistema
     * de quem ele paga está quebrado.
     */
    anvisaInstavel() {
      const comFalha = this.missing.find((item) => item.reason_code === 'ANVISA_UNAVAILABLE');

      return comFalha ? comFalha.reason : null;
    },
    blockedTitle() {
      if (this.anvisaInstavel) {
        return 'A Anvisa não conseguiu gerar a numeração agora.';
      }

      if (this.missing.length === 1) {
        return `Sem numeração disponível para ${this.missing[0].regulatory_type_description}.`;
      }

      return 'Sem numeração disponível para alguns dos receituários desta prescrição.';
    },
    blockedVisa() {
      return this.missing.some((item) => ['NRA', 'NRB', 'NRB2', 'NRR', 'NRT'].includes(item.regulatory_type));
    },
  },
  methods: {
    badgeFor(document) {
      return classificationFromType(document.regulatory_type);
    },
    numberingLine(document) {
      if (document.sncr_number) {
        return `Consome a numeração ${document.sncr_number}`;
      }

      if (!requiresSncrNumber(document.regulatory_type)) {
        return null;
      }

      const nome = regulatoryTypeName(document.regulatory_type, document.regulatory_type_description);

      if (!document.sncr) {
        return `Consome 1 numeração de ${nome}`;
      }

      if (!document.sncr.sufficient) {
        return document.sncr.reason ?? `Sem numeração disponível para ${nome}`;
      }

      return `Consome 1 numeração de ${nome} — ${document.sncr.balance} em estoque`;
    },
    authenticate() {
      if (!this.professionalId) {
        return Alert.toastError('Não identificamos seu cadastro de profissional para autenticar no gov.br.');
      }

      this.authenticating = true;

      const destino = this.returnTo ?? {path: this.$route.fullPath, professionalId: this.professionalId};

      startGovBrAuth(this.professionalId, destino).catch(() => {
        this.authenticating = false;
        Alert.toastError('Não foi possível abrir a autenticação do gov.br. Tente novamente.');
      });
    },
    /**
     * Emitir controlado sem numeração é exceção, não atalho: confirma antes,
     * dizendo o que o documento deixa de ser.
     */
    confirmWithoutNumbering() {
      const tipos = this.missing.map((item) => item.regulatory_type_description).join(', ');

      Swal.fire({
        title: 'Imprimir sem numeração da Anvisa?',
        html: `<p style="text-align:left">A numeração do SNCR não foi obtida para: <b>${tipos || 'este receituário'}</b>.</p>`
            + '<p style="text-align:left">O documento será gerado <b>sem a numeração oficial</b> exigida pela '
            + '<b>RDC 1.000/2025</b> para receituário controlado. A dispensação não poderá ser registrada no SNCR '
            + 'e a farmácia pode recusar a receita.</p>'
            + '<p style="text-align:left">A responsabilidade pela emissão nessas condições é do prescritor.</p>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Imprimir mesmo assim',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary',
        },
      }).then((result) => {
        if (result.isConfirmed) {
          this.emitGenerate(true);
        }
      });
    },
    emitGenerate(allowWithoutNumbering = false) {
      const certifier = this.certifiers.find((item) => item.id === this.selectedCertifier) ?? null;

      if (certifier) {
        this.$store.state.auth.certifier = certifier;
      }

      this.$emit('generate', certifier, {allowWithoutNumbering});
    },
  },
}
</script>

<style scoped>
.plan-intro {
  color: #495057;
  margin-bottom: 16px;
}

.plan-document {
  border: 1px solid #e9ecef;
  border-left: 3px solid #780f5a;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}

.plan-document-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.plan-document-name {
  font-weight: 700;
  color: #212529;
}

.plan-document-items {
  margin: 0 0 6px 0;
  padding-left: 20px;
  color: #495057;
}

.plan-document-manual {
  font-size: 0.75rem;
  color: #7a4f00;
  margin-left: 6px;
}

.plan-document-numbering {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: #495057;
  font-size: 0.85rem;
}

.plan-blocked {
  border: 1px solid #f3c0c0;
  border-left: 3px solid #c92a2a;
  border-radius: 8px;
  background: #fff5f5;
  padding: 12px 16px;
  margin-top: 16px;
}

/* Faixa de instabilidade da Anvisa. Fica acima do título porque muda o sentido
   do que vem abaixo: não é o prescritor que precisa fazer algo. */
.plan-anvisa-down {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  padding: 10px 12px;
  border-left: 3px solid #f1b44c;
  background: #fff8e7;
  border-radius: 4px;
  font-size: 0.86rem;
  line-height: 1.45;
  color: #6b4f14;
}

.plan-anvisa-down i {
  flex: 0 0 auto;
  margin-top: 1px;
  color: #d99413;
}

.plan-blocked-title {
  font-weight: 700;
  color: #212529;
  margin: 0 0 6px 0;
}

.plan-blocked-text {
  color: #495057;
  margin: 0 0 8px 0;
}

.plan-blocked-more {
  margin-top: 12px;
  border-top: 1px solid #f3c0c0;
  padding-top: 8px;
}

.plan-blocked-toggle {
  padding: 0;
  color: #495057;
  font-weight: 600;
  text-decoration: none;
}

.plan-blocked-menu {
  margin-top: 8px;
}

.plan-blocked-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.plan-blocked-draft {
  color: #868e96;
  font-size: 0.85rem;
}

.plan-document-numbering-missing {
  color: #8b1a1a;
  font-weight: 600;
}

.plan-certifier {
  border-top: 1px solid #e9ecef;
  padding-top: 16px;
  margin-top: 16px;
}

.plan-certifier-inline {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
  margin-bottom: 8px;
}
</style>
